import { supabase } from '@/lib/supabase';
import { getAdminRealm } from '@/lib/adminAuth';
import { brandForRealm } from '@/lib/adminBrand';
import { templatesForApi } from '@/data/contentTemplates';

/** White-label brand context forwarded to the AI generation edge functions. */
function generationBrand() {
  return brandForRealm(getAdminRealm()).generation;
}



export type AssetType = 'image' | 'video' | 'social_profile' | 'social_post' | 'reference_url';
export type AssetStatus = 'pending' | 'analyzing' | 'analyzed' | 'failed';
export type PostStatus = 'draft' | 'approved' | 'scheduled' | 'published' | 'failed';
export type WeekStatus = 'draft' | 'approved' | 'scheduled' | 'published';

export interface BrandAsset {
  id: string;
  asset_type: AssetType;
  title?: string | null;
  url: string;
  storage_path?: string | null;
  source_platform?: string | null;
  notes?: string | null;
  tags?: string[];
  analysis?: any;
  analyzed_at?: string | null;
  status: AssetStatus;
  created_at: string;
}

export interface BrandVoiceProfile {
  id: string;
  name: string;
  summary: string;
  tone: string[];
  vocabulary: string[];
  avoid_words: string[];
  themes: string[];
  visual_palette: string[];
  visual_style: string;
  signature_phrases: string[];
  hashtag_strategy: string[];
  example_captions: string[];
  source_asset_ids: string[];
  raw_analysis: any;
  is_active: boolean;
  created_at: string;
}

export interface GeneratedWeek {
  id: string;
  brand_voice_id: string | null;
  week_label: string | null;
  status: WeekStatus;
  notes?: string | null;
  created_at: string;
}

export type ApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected';

export interface ScheduledPost {
  id: string;
  week_id: string | null;
  brand_voice_id: string | null;
  day_label: string | null;
  scheduled_for: string | null;
  platform: string;
  post_type: string;
  theme: string | null;
  caption: string | null;
  hashtags: string[];
  cta: string | null;
  image_url: string | null;
  image_prompt: string | null;
  video_url: string | null;
  status: PostStatus;
  order_index: number;
  approval_status: ApprovalStatus;
  admin_feedback: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

// ---------- Assets ----------

export async function listAssets(): Promise<BrandAsset[]> {
  const { data } = await supabase
    .from('brand_assets')
    .select('*')
    .eq('realm', getAdminRealm())
    .order('created_at', { ascending: false });
  return (data as BrandAsset[]) || [];
}

export async function uploadAssetFile(file: File): Promise<{ url: string; path: string } | null> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('brand-assets').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) return null;
  const { data } = supabase.storage.from('brand-assets').getPublicUrl(path);
  return { url: data.publicUrl, path };
}

export async function createAsset(asset: Partial<BrandAsset>): Promise<BrandAsset | null> {
  const { data, error } = await supabase
    .from('brand_assets')
    .insert({
      asset_type: asset.asset_type,
      title: asset.title || null,
      url: asset.url,
      storage_path: asset.storage_path || null,
      source_platform: asset.source_platform || null,
      notes: asset.notes || null,
      tags: asset.tags || [],
      status: 'pending',
      realm: getAdminRealm(),
    })
    .select('*')
    .single();
  if (error) return null;
  return data as BrandAsset;
}

export async function deleteAsset(id: string): Promise<void> {
  await supabase.from('brand_assets').delete().eq('id', id);
}

// ---------- Brand voice ----------

export async function listVoices(): Promise<BrandVoiceProfile[]> {
  const { data } = await supabase
    .from('brand_voice_profiles')
    .select('*')
    .eq('realm', getAdminRealm())
    .order('created_at', { ascending: false });
  return (data as BrandVoiceProfile[]) || [];
}

export async function analyzeAssets(assetIds: string[], voiceName?: string): Promise<BrandVoiceProfile | null> {
  if (assetIds.length === 0) throw new Error('Select at least one asset to learn from.');
  const { data: assets } = await supabase
    .from('brand_assets')
    .select('*')
    .in('id', assetIds);
  if (!assets || assets.length === 0) throw new Error('Selected assets could not be loaded. Try refreshing.');

  // Mark as analyzing
  await supabase.from('brand_assets').update({ status: 'analyzing' }).in('id', assetIds);

  const { data, error } = await supabase.functions.invoke('analyze-brand-voice', {
    body: {
      assets: assets.map((a: any) => ({
        id: a.id,
        asset_type: a.asset_type,
        title: a.title,
        url: a.url,
        source_platform: a.source_platform,
        notes: a.notes,
      })),
      voice_name: voiceName,
      // White-label brand context so each realm's brain learns ITS OWN brand
      // (e.g. mtnshop learns The MTN Shop, never Zera Travel / Albania).
      brand: generationBrand(),
    },
  });


  if (error || !data?.voice) {
    await supabase.from('brand_assets').update({ status: 'failed' }).in('id', assetIds);
    const msg =
      (data && (data.error as string)) ||
      (error && (error.message as string)) ||
      'The AI analysis service did not return a result. Please try again.';
    throw new Error(msg);
  }

  // Save the voice profile
  const { data: saved, error: saveErr } = await supabase
    .from('brand_voice_profiles')
    .insert({
      ...data.voice,
      is_active: true,
      realm: getAdminRealm(),
    })
    .select('*')
    .single();

  if (saveErr || !saved) {
    await supabase.from('brand_assets').update({ status: 'failed' }).in('id', assetIds);
    throw new Error(saveErr?.message || 'The brand voice was generated but could not be saved.');
  }

  // Mark assets as analyzed
  await supabase
    .from('brand_assets')
    .update({ status: 'analyzed', analyzed_at: new Date().toISOString() })
    .in('id', assetIds);

  return saved as BrandVoiceProfile;
}


export async function deleteVoice(id: string): Promise<void> {
  await supabase.from('brand_voice_profiles').delete().eq('id', id);
}

// ---------- Week generation ----------
//
// Generates EXACTLY 7 text posts (Mon–Sun) for one week and inserts them as a
// single batch. Images are NOT created here — the caller is responsible for
// painting one AI image per post afterwards (see generatePostImage). This keeps
// generation to a single source of truth and prevents the old bug where a
// "generate images" toggle produced a second, image-less batch of posts.
//
// WORKFLOW: the Albania and Romania Social Hubs follow the weekly HOTEL STORY
// ARC — Monday introduces a specific location in the realm's country, Tuesday
// a specific hotel there, Wednesday that hotel's amenities/offerings, Thursday
// the local activities/restaurants/events around it, Friday a culture &
// heritage deep dive, and the weekend expands on the city, hotel and culture.
// Other realms (e.g. the white-label real-estate hub) keep the default
// template-deal generation.
export async function generateWeek(
  voice: BrandVoiceProfile,
  weekLabel?: string,
): Promise<{ week: GeneratedWeek; posts: ScheduledPost[] } | null> {
  const realm = getAdminRealm();
  const workflow = realm === 'albania' || realm === 'romania' ? 'hotel_week' : undefined;

  const { data, error } = await supabase.functions.invoke('generate-week-from-voice', {
    // templates = the 25-item content template library (single source of truth
    // in src/data/contentTemplates.ts) for the ACTIVE realm. The function deals
    // 7 distinct templates across the week (max ONE people-focused template),
    // mixing hero/supporting shots. brand = white-label generation context.
    // workflow = 'hotel_week' switches the travel realms onto the Mon–Sun
    // location→hotel story arc described above.
    body: { voice, week_label: weekLabel, templates: templatesForApi(), brand: generationBrand(), workflow },
  });



  if (error || !data?.posts) return null;


  // Posts are scheduled Mon–Sun starting next Monday, so compute the schedule
  // window FIRST and name the week by the dates it covers, e.g. "6/22/26 – 6/28/26".
  const nextMonday = (() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 1 : 8 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(9, 0, 0, 0);
    return d;
  })();
  const weekEnd = new Date(nextMonday);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const rangeLabel = `${fmtShortDate(nextMonday.toISOString())} – ${fmtShortDate(weekEnd.toISOString())}`;

  const { data: weekRow } = await supabase
    .from('generated_content_weeks')
    .insert({
      brand_voice_id: voice.id,
      week_label: weekLabel || rangeLabel,
      status: 'draft',
      realm: getAdminRealm(),
    })
    .select('*')
    .single();
  if (!weekRow) return null;

  const dayMap: Record<string, number> = {
    Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
  };


  const rows = (data.posts as any[]).map((p, i) => {
    const dayOffset = dayMap[p.day_label] ?? i;
    const scheduled = new Date(nextMonday);
    scheduled.setDate(scheduled.getDate() + dayOffset);
    // Parse time like "9:00 AM"
    const m = String(p.scheduled_time || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (m) {
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = (m[3] || '').toUpperCase();
      if (ampm === 'PM' && h < 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      scheduled.setHours(h, min, 0, 0);
    }
    return {
      week_id: weekRow.id,
      brand_voice_id: voice.id,
      day_label: p.day_label || null,
      scheduled_for: scheduled.toISOString(),
      platform: 'instagram',
      post_type: p.post_type || 'Feed Post',
      theme: p.theme || null,
      caption: p.caption || null,
      hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
      cta: p.cta || null,
      image_url: p.image_url || null,
      image_prompt: p.image_prompt || null,
      status: 'draft' as PostStatus,
      approval_status: 'draft' as ApprovalStatus,
      order_index: i,
      realm: getAdminRealm(),
    };
  });

  const { data: postRows } = await supabase
    .from('scheduled_posts')
    .insert(rows)
    .select('*');

  return {
    week: weekRow as GeneratedWeek,
    posts: (postRows as ScheduledPost[]) || [],
  };
}

// ---------- Scheduled posts ----------

export async function listScheduledPosts(weekId?: string): Promise<ScheduledPost[]> {
  let q = supabase.from('scheduled_posts').select('*').eq('realm', getAdminRealm()).order('scheduled_for', { ascending: true });
  if (weekId) q = q.eq('week_id', weekId);
  const { data } = await q;
  return (data as ScheduledPost[]) || [];
}

export async function listWeeks(): Promise<GeneratedWeek[]> {
  const { data } = await supabase
    .from('generated_content_weeks')
    .select('*')
    .eq('realm', getAdminRealm())
    .order('created_at', { ascending: false });
  return (data as GeneratedWeek[]) || [];
}

export async function updateScheduledPost(id: string, patch: Partial<ScheduledPost>): Promise<void> {
  const allowed: any = {};
  ['day_label', 'scheduled_for', 'platform', 'post_type', 'theme', 'caption', 'hashtags', 'cta', 'image_url', 'image_prompt', 'status', 'order_index', 'approval_status', 'admin_feedback', 'reviewed_at', 'reviewed_by'].forEach((k) => {
    if (k in patch) allowed[k] = (patch as any)[k];
  });
  allowed.updated_at = new Date().toISOString();
  await supabase.from('scheduled_posts').update(allowed).eq('id', id);
}

export async function deleteScheduledPost(id: string): Promise<void> {
  await supabase.from('scheduled_posts').delete().eq('id', id);
}

/**
 * Admin approves a post. Marks it approved and ready for scheduling/publishing.
 */
export async function approvePost(id: string, reviewer: string, feedback?: string): Promise<void> {
  await updateScheduledPost(id, {
    approval_status: 'approved',
    admin_feedback: feedback?.trim() ? feedback.trim() : null,
    reviewed_at: new Date().toISOString(),
    reviewed_by: reviewer,
    status: 'approved',
  });
}

/**
 * Admin disapproves a post. Records feedback and sends the post back a step
 * (status -> 'draft') so the team can revise it in Step 3.
 */
export async function rejectPost(id: string, reviewer: string, feedback: string): Promise<void> {
  await updateScheduledPost(id, {
    approval_status: 'rejected',
    admin_feedback: feedback.trim(),
    reviewed_at: new Date().toISOString(),
    reviewed_by: reviewer,
    status: 'draft',
  });
}

/**
 * Sends a post (Step 3) to the admin for approval (Step 4). Marks approval_status
 * 'pending' so it leaves the editor queue and appears in the admin review board.
 * Clears any stale rejection feedback from a previous round.
 */
export async function submitForApproval(id: string): Promise<void> {
  await updateScheduledPost(id, {
    approval_status: 'pending',
    admin_feedback: null,
    reviewed_at: null,
    reviewed_by: null,
    status: 'draft',
  });
}

/**
 * Generate a brand-voice-aware image for a single post that is missing one,
 * upload it to storage, persist it on the post, and return the public URL.
 */
export async function generatePostImage(
  post: ScheduledPost,
  voice: BrandVoiceProfile | null,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('generate-post-image', {
    body: {
      post: {
        caption: post.caption,
        theme: post.theme,
        post_type: post.post_type,
        day_label: post.day_label,
        image_prompt: post.image_prompt,
      },
      voice: voice
        ? {
            visual_style: voice.visual_style,
            visual_palette: voice.visual_palette,
            themes: voice.themes,
            tone: voice.tone,
          }
        : null,
    },
  });

  if (error || !data?.image_url) {
    throw new Error((data && (data.error as string)) || error?.message || 'Image generation failed.');
  }

  const dataUrl: string = data.image_url;

  // Convert the returned base64 data URL into a stored, durable public URL.
  let publicUrl = dataUrl;
  try {
    if (dataUrl.startsWith('data:')) {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const path = `generated/${post.id}-${Date.now()}.png`;
      const { error: upErr } = await supabase.storage
        .from('brand-assets')
        .upload(path, blob, { cacheControl: '3600', upsert: true, contentType: blob.type || 'image/png' });
      if (!upErr) {
        const { data: pub } = supabase.storage.from('brand-assets').getPublicUrl(path);
        if (pub?.publicUrl) publicUrl = pub.publicUrl;
      }
    }
  } catch {
    // Fall back to the raw data URL if upload fails.
  }

  await updateScheduledPost(post.id, {
    image_url: publicUrl,
    image_prompt: data.prompt || post.image_prompt,
  });

  return publicUrl;
}

/**
 * Uploads a base64 data URL to the brand-assets bucket and returns a durable
 * public URL. Falls back to the original data URL if the upload fails.
 */
async function persistDataUrl(dataUrl: string, postId: string): Promise<string> {
  try {
    if (!dataUrl.startsWith('data:')) return dataUrl;
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const path = `generated/${postId}-${Date.now()}.png`;
    const { error: upErr } = await supabase.storage
      .from('brand-assets')
      .upload(path, blob, { cacheControl: '3600', upsert: true, contentType: blob.type || 'image/png' });
    if (!upErr) {
      const { data: pub } = supabase.storage.from('brand-assets').getPublicUrl(path);
      if (pub?.publicUrl) return pub.publicUrl;
    }
  } catch {
    /* fall through */
  }
  return dataUrl;
}

/**
 * Fully regenerates a single post: fresh caption, hashtags, CTA, theme AND a
 * brand-matched AI image. Persists everything and returns the applied patch so
 * the editor can update its live draft.
 */
export async function regeneratePost(
  post: ScheduledPost,
  voice: BrandVoiceProfile | null,
): Promise<Partial<ScheduledPost>> {
  const { data, error } = await supabase.functions.invoke('regenerate-post', {
    body: {
      post: {
        caption: post.caption,
        theme: post.theme,
        post_type: post.post_type,
        day_label: post.day_label,
        image_prompt: post.image_prompt,
        hashtags: post.hashtags,
      },
      voice: voice
        ? {
            summary: voice.summary,
            tone: voice.tone,
            vocabulary: voice.vocabulary,
            avoid_words: voice.avoid_words,
            signature_phrases: voice.signature_phrases,
            themes: voice.themes,
            visual_style: voice.visual_style,
            visual_palette: voice.visual_palette,
            hashtag_strategy: voice.hashtag_strategy,
          }
        : null,
      // Content template library: the function keeps the post inside its
      // template (recovered from the theme prefix) but re-rolls the shot
      // (hero vs supporting angle) for a genuinely fresh image.
      templates: templatesForApi(),
      brand: generationBrand(),

    },
  });


  if (error || !data || data.error) {
    throw new Error((data && (data.error as string)) || error?.message || 'Post regeneration failed.');
  }

  let imageUrl = post.image_url;
  if (data.image_url) {
    imageUrl = await persistDataUrl(data.image_url as string, post.id);
  }

  const patch: Partial<ScheduledPost> = {
    caption: data.caption ?? post.caption,
    hashtags: Array.isArray(data.hashtags) && data.hashtags.length ? data.hashtags : post.hashtags,
    cta: data.cta ?? post.cta,
    theme: data.theme ?? post.theme,
    image_prompt: data.image_prompt ?? post.image_prompt,
    image_url: imageUrl,
  };

  await updateScheduledPost(post.id, patch);
  return patch;
}



export async function deleteWeek(id: string): Promise<void> {
  // Remove the week's posts first so no orphaned posts linger in the editor.
  await supabase.from('scheduled_posts').delete().eq('week_id', id);
  await supabase.from('generated_content_weeks').delete().eq('id', id);
}

// ---------- Week labeling helpers ----------
// Weeks are named by the date range covered by their 7 posts, e.g. "6/22/26 – 6/28/26".

/** Formats an ISO date as M/D/YY (e.g. 6/22/26). */
export function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

/** Builds a "6/22/26 – 6/28/26" range label from a set of posts' scheduled dates. */
export function weekDateRangeLabel(posts: ScheduledPost[]): string | null {
  const times = posts
    .map((p) => (p.scheduled_for ? new Date(p.scheduled_for).getTime() : NaN))
    .filter((t) => !isNaN(t));
  if (times.length === 0) return null;
  const start = fmtShortDate(new Date(Math.min(...times)).toISOString());
  const end = fmtShortDate(new Date(Math.max(...times)).toISOString());
  if (!start || !end) return null;
  return start === end ? start : `${start} – ${end}`;
}

/**
 * Display name for a week in menus: prefers the date range of its posts,
 * then the stored label, then a short id.
 */
export function weekDisplayLabel(week: GeneratedWeek, allPosts: ScheduledPost[]): string {
  const range = weekDateRangeLabel(allPosts.filter((p) => p.week_id === week.id));
  if (range) return range;
  // Stored label may already be a date range (new weeks) or a legacy name.
  return week.week_label || `Week ${week.id.slice(0, 8)}`;
}

